/**
 * Public storefront card grid (TNK-105 phase 2). Renders the real card
 * photo in a 2.5:3.5 slot — the Figma design's procedural gradient art
 * was a stand-in for the absent inventory image, which we have. The card
 * chrome (rarity glow, holographic shimmer, foil indicator, gold price)
 * carries over verbatim.
 *
 * Tier colors are inline because they're per-card; everything else uses
 * the `.shop-theme`-shadowed Tailwind tokens so a future palette tweak
 * lands in one place.
 */

import { Link } from "@tanstack/react-router";
import { ShoppingCart, Sparkles, Star, Zap } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";

import { imageUrl } from "@/features/imageUpload";
import {
  storefrontPrice,
  type StorefrontBundle,
  type StorefrontCard,
} from "@/features/storefront";
import { isFoil, visualRarity, type ShopRarity } from "@/features/shopFilters";
import { BundleCard } from "./BundleCard";

interface CardGridProps {
  cards: StorefrontCard[];
  onAddToCart: (card: StorefrontCard) => void;
  cartIds: string[];
  /** When provided, a plain click on a card calls this instead of navigating.
   *  Modifier-clicks (cmd / ctrl / shift / middle button) still navigate to
   *  the full page via the underlying Link, so deep-link sharing keeps
   *  working. Omit on routes that don't have a modal. TNK-115 phase B2. */
  onOpenCard?: (card: StorefrontCard) => void;
  /** TNK-212 phase 2a — locked-bundle tiles pinned to the FRONT of the same
   *  grid so bundles blend in with the singles. The caller passes [] when a
   *  card-attribute facet/search is active (bundles hide under filters). */
  bundles?: StorefrontBundle[];
}

const RARITY_CHROME: Record<
  ShopRarity,
  { color: string; glow: string; icon: React.ReactNode }
> = {
  // TNK-165 follow-up — operator asked for ~10% more glow on /shop/inventory
  // cards. Each alpha bumped by ×1.10 (clamped at 0.88 for Legendary which
  // would otherwise round into hard saturation). Blur radii unchanged so
  // the rarity hierarchy reads the same — just slightly more presence.
  Common: {
    color: "#7a8aaa",
    glow: "0 0 8px rgba(122,138,170,0.33)",
    icon: null,
  },
  Uncommon: {
    color: "#4ade80",
    glow: "0 0 8px rgba(74,222,128,0.44)",
    icon: null,
  },
  Rare: {
    color: "#4d9ef7",
    glow: "0 0 12px rgba(77,158,247,0.55)",
    icon: <Zap size={10} />,
  },
  "Ultra Rare": {
    color: "#c9a84c",
    glow: "0 0 16px rgba(201,168,76,0.66)",
    icon: <Star size={10} />,
  },
  "Secret Rare": {
    color: "#a855f7",
    glow: "0 0 20px rgba(168,85,247,0.77)",
    icon: <Sparkles size={10} />,
  },
  Legendary: {
    color: "#f0c96e",
    glow: "0 0 24px rgba(240,201,110,0.88)",
    icon: <Sparkles size={10} />,
  },
};

// Short rarity labels (TNK-212) so the chip stays one uniform line — the full
// "Ultra Rare" / "Secret Rare" / "Legendary" labels wrapped and made tiles
// uneven. The full name rides along as a hover title for clarity.
const RARITY_ABBREV: Record<ShopRarity, string> = {
  Common: "C",
  Uncommon: "U/C",
  Rare: "R",
  "Ultra Rare": "UR",
  "Secret Rare": "SR",
  Legendary: "LEG",
};

// Condition is operator-controlled vocabulary (NM / LP / MP / HP / DMG and
// occasional verbose variants). Match by leading-letters so legacy values
// still get colored sanely.
function conditionColor(condition: string): string {
  const c = condition.toUpperCase();
  if (c.startsWith("NM") || c.startsWith("M")) return "#4ade80";
  if (c.startsWith("LP")) return "#a3e635";
  if (c.startsWith("MP")) return "#facc15";
  if (c.startsWith("HP")) return "#fb923c";
  if (c.startsWith("DMG") || c.startsWith("D")) return "#f87171";
  return "#7a8aaa";
}

const GAME_ACCENT: Record<string, string> = {
  Pokemon: "#ff3b3b",
  Magic: "#8b5cf6",
  "Yu-Gi-Oh": "#f59e0b",
  "Flesh & Blood": "#ef4444",
  Lorcana: "#3b82f6",
};

function CardItem({
  card,
  inCart,
  onAddToCart,
  onOpenCard,
  index,
}: {
  card: StorefrontCard;
  inCart: boolean;
  onAddToCart: (c: StorefrontCard) => void;
  onOpenCard?: (c: StorefrontCard) => void;
  index: number;
}) {
  const rarity = visualRarity(card);
  const chrome = RARITY_CHROME[rarity];
  const foil = isFoil(card.finish);
  const showShimmer =
    rarity === "Secret Rare" || rarity === "Legendary" || foil;
  const condColor = conditionColor(card.condition);
  const gameStripe = GAME_ACCENT[card.game] ?? chrome.color;
  // TNK-125: thumb variant — grid cells render at ~180×252.
  const img = card.imageFrontRef ? imageUrl(card.imageFrontRef, "thumb") : null;

  return (
    <motion.div
      // `layout` removed (TNK-115 polish): Framer's layout measurement was
      // racing with CSS grid placement during the staggered entrance,
      // leaving 2-3 first-row cards mid-transition with off width + height.
      // We don't actually need `layout` here — entrance is handled by
      // initial/animate explicit values, and filter swaps animate via the
      // AnimatePresence wrapper above.
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.9 }}
      // Stagger entrance but cap so filter changes that introduce many new
      // rows don't take forever — every ~12 cards the delay loops back to 0.
      transition={{ duration: 0.25, delay: (index % 12) * 0.03 }}
      className="h-full"
    >
      <Link
        to="/shop/card/$id"
        params={{ id: card.id }}
        className="group relative flex h-full flex-col overflow-hidden rounded-lg border border-border bg-card transition-all hover:-translate-y-1"
        style={{
          boxShadow: "0 4px 16px rgba(0,0,0,0.4)",
        }}
        onClick={(e) => {
          // TNK-115 phase B2: when the host page wants the modal, a plain
          // left-click opens the modal in place and we suppress the Link
          // navigation. Modifier-clicks (cmd / ctrl / shift / meta) and
          // middle-button (button === 1, handled by browser default)
          // still navigate to the full /shop/card/$id page — keeps deep-
          // link sharing + "open in new tab" intuitive.
          if (!onOpenCard) return;
          if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
          if (e.button !== 0) return;
          e.preventDefault();
          onOpenCard(card);
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.borderColor = `${chrome.color}66`;
          e.currentTarget.style.boxShadow = `0 8px 32px rgba(0,0,0,0.6), ${chrome.glow}`;
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.borderColor = "";
          e.currentTarget.style.boxShadow = "0 4px 16px rgba(0,0,0,0.4)";
        }}
      >
        {/* Game color accent stripe */}
        <div
          className="absolute top-0 left-0 right-0 h-0.5"
          style={{
            background: `linear-gradient(90deg, transparent, ${gameStripe}, transparent)`,
          }}
        />

        {/* Image slot — real photo at 2.5:3.5 (standard TCG card aspect) */}
        <div className="p-2 pb-1">
          <div
            className="relative w-full overflow-hidden rounded bg-muted"
            style={{ aspectRatio: "2.5 / 3.5" }}
          >
            {img && (
              <img
                src={img}
                alt={card.name}
                className="h-full w-full object-cover"
                loading="lazy"
              />
            )}
            {showShimmer && <div className="absolute inset-0 shop-holo" />}
            {foil && (
              <div className="absolute right-2 top-2">
                <div
                  className="flex h-5 w-5 items-center justify-center rounded-full"
                  style={{
                    background:
                      "linear-gradient(135deg, #c9a84c, #a855f7, #4d9ef7)",
                    boxShadow: "0 0 8px rgba(201,168,76,0.6)",
                  }}
                >
                  <Sparkles size={10} className="text-white" />
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Info */}
        <div className="flex flex-col gap-1.5 px-3 pb-3">
          <div>
            <h3
              className="shop-display truncate leading-tight transition-colors"
              style={{
                fontSize: "13px",
                fontWeight: 600,
                color: "var(--color-foreground)",
              }}
            >
              {card.name}
            </h3>
            <p className="mt-0.5 truncate text-[10px] text-muted-foreground">
              {card.setName}
              {card.setCode ? ` · ${card.setCode}` : ""}
              {card.cardNumber ? ` · ${card.cardNumber}` : ""}
            </p>
          </div>

          {/* Badges row — rarity, condition, type */}
          <div className="flex flex-wrap items-center gap-1.5">
            <span
              title={rarity}
              className="flex items-center gap-1 rounded-sm px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider"
              style={{
                background: `${chrome.color}1a`,
                border: `1px solid ${chrome.color}44`,
                color: chrome.color,
              }}
            >
              {chrome.icon}
              {RARITY_ABBREV[rarity]}
            </span>
            <span
              className="shop-mono rounded-sm px-1.5 py-0.5 text-[9px] font-medium"
              style={{
                background: `${condColor}1a`,
                border: `1px solid ${condColor}44`,
                color: condColor,
              }}
            >
              {card.condition}
            </span>
            {card.tcgSupertype && (
              <span
                className="rounded-sm px-1.5 py-0.5 text-[9px]"
                style={{
                  background: "rgba(77,158,247,0.1)",
                  border: "1px solid rgba(77,158,247,0.25)",
                  color: "#4d9ef7",
                }}
              >
                {card.tcgSupertype}
              </span>
            )}
          </div>

          {/* Price + Add to cart */}
          <div className="mt-1 flex items-center justify-between">
            <span
              className="shop-mono text-base font-bold"
              style={{ color: "var(--color-primary)" }}
            >
              ${storefrontPrice(card).toFixed(2)}
            </span>
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onAddToCart(card);
              }}
              className="flex items-center gap-1 rounded-sm px-2.5 py-1.5 text-[11px] font-semibold transition-colors"
              style={{
                background: inCart
                  ? "rgba(74,222,128,0.15)"
                  : "rgba(201,168,76,0.15)",
                border: inCart
                  ? "1px solid rgba(74,222,128,0.4)"
                  : "1px solid rgba(201,168,76,0.35)",
                color: inCart ? "#4ade80" : "var(--color-primary)",
              }}
            >
              <ShoppingCart size={11} />
              {inCart ? "Added" : "Add"}
            </button>
          </div>
        </div>
      </Link>
    </motion.div>
  );
}

export function CardGrid({
  cards,
  onAddToCart,
  cartIds,
  onOpenCard,
  bundles = [],
}: CardGridProps) {
  if (cards.length === 0 && bundles.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center gap-3 py-24">
        <p className="shop-display text-lg text-muted-foreground">
          No cards found
        </p>
        <p className="text-sm text-muted-foreground">
          Try adjusting your filters
        </p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
      {/* Bundle tiles lead the grid (TNK-212 phase 2a). Outside AnimatePresence
          — they're a small, stable set, not part of the card filter churn. */}
      {bundles.map((b) => (
        <BundleCard key={b.id} bundle={b} />
      ))}
      <AnimatePresence mode="popLayout">
        {cards.map((card, i) => (
          <CardItem
            key={card.id}
            card={card}
            inCart={cartIds.includes(card.id)}
            onAddToCart={onAddToCart}
            onOpenCard={onOpenCard}
            index={i}
          />
        ))}
      </AnimatePresence>
    </div>
  );
}
